import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import DataTable from './Datatable';
import DeleteConfirm from '../componentes/DeleteConfirm';
import { MapPin, ExternalLink } from 'lucide-react';

const tiempoRelativo = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha.replace(' ', 'T'));
  if (isNaN(d.getTime())) return fecha;
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'Hace instantes';
  if (min < 60) return `Hace ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `Hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `Hace ${dias} día${dias !== 1 ? 's' : ''}`;
};

/** Elimina ubicaciones sobrantes dejando solo las 10 más recientes por mascota */
const pruneUbicaciones = (data) => {
  const grouped = data.reduce((acc, ubi) => {
    if (!acc[ubi.id_mascota]) acc[ubi.id_mascota] = [];
    acc[ubi.id_mascota].push(ubi);
    return acc;
  }, {});
  const toDelete = [];
  const toKeep = [];

  Object.values(grouped).forEach((arr) => {
    arr.sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora));
    arr.forEach((ubi, idx) => {
      if (idx < 10) toKeep.push(ubi);
      else toDelete.push(ubi.id);
    });
  });
  return { toKeep, toDelete };
};

const UbicacionesPanel = () => {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [mascotas, setMascotas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);        // para eliminación individual
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');           // filtro fecha inicio
  const [toDate, setToDate] = useState('');               // filtro fecha fin
  const [selectedIds, setSelectedIds] = useState([]);     // selección múltiple
  const [showBatchDelete, setShowBatchDelete] = useState(false);

  const fetchUbicaciones = async () => {
    try {
      setLoading(true);
      const [ubiRes, mascRes, usuRes] = await Promise.all([
        api.getUbicaciones(),
        api.getMascotas(),
        api.getUsuarios(),
      ]);
      const ubis = ubiRes.data?.data || ubiRes.data || [];
      const mascs = mascRes.data?.data || mascRes.data || [];
      const usus = usuRes.data?.data || usuRes.data || [];

      const { toKeep, toDelete } = pruneUbicaciones(ubis);
      setUbicaciones(toKeep);
      setMascotas(mascs);
      setUsuarios(usus);

      toDelete.forEach((id) =>
        api.deleteUbicacion(id).catch(() => { })
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUbicaciones();
  }, []);

  const mascotaMap = useMemo(
    () => Object.fromEntries(mascotas.map((m) => [m.id, m])),
    [mascotas]
  );
  const usuarioMap = useMemo(
    () => Object.fromEntries(usuarios.map((u) => [u.id, u])),
    [usuarios]
  );

  // Filtro combinado: búsqueda + rango de fechas
  const filtered = useMemo(() => {
    let result = ubicaciones;

    // Filtro por texto
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter((ubi) => {
        const mascota = mascotaMap[ubi.id_mascota];
        const mascName = mascota?.nombre?.toLowerCase() || '';
        const userId = mascota?.id_usuario;
        const usuario = userId ? usuarioMap[userId] : null;
        const ownerName = usuario
          ? `${usuario.nombre} ${usuario.apellido}`.toLowerCase()
          : '';
        const lugar = ubi.lugar?.toLowerCase() || '';
        return (
          mascName.includes(term) ||
          ownerName.includes(term) ||
          lugar.includes(term) ||
          String(ubi.id_mascota).includes(term)
        );
      });
    }

    // Filtro por fecha desde
    if (fromDate) {
      const from = new Date(fromDate);
      result = result.filter((ubi) => {
        const d = new Date(ubi.fecha_hora.replace(' ', 'T'));
        return d >= from;
      });
    }
    // Filtro por fecha hasta
    if (toDate) {
      const to = new Date(toDate + 'T23:59:59'); // incluye todo el día
      result = result.filter((ubi) => {
        const d = new Date(ubi.fecha_hora.replace(' ', 'T'));
        return d <= to;
      });
    }

    return result;
  }, [ubicaciones, search, fromDate, toDate, mascotaMap, usuarioMap]);

  // --- Manejo de selección múltiple ---
  const handleSelectRow = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (filtered.length === 0) return;
    const allIds = filtered.map(row => row.id);
    setSelectedIds(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleBatchDelete = () => {
    if (selectedIds.length === 0) return;
    setShowBatchDelete(true);
  };

  const confirmBatchDelete = async () => {
    try {
      // Eliminar uno por uno (podría optimizarse con endpoint bulk si existe)
      await Promise.all(selectedIds.map(id => api.deleteUbicacion(id)));
      setShowBatchDelete(false);
      setSelectedIds([]);
      fetchUbicaciones();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  // --- Eliminación individual (mantenida) ---
  const handleDelete = (id) => {
    setSelected(ubicaciones.find((u) => u.id === id));
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    try {
      await api.deleteUbicacion(selected.id);
      setShowDelete(false);
      fetchUbicaciones();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const columns = [
    {
      key: 'id_mascota',
      label: 'Dueño',
      id: 'col-dueno',
      render: (_, row) => {
        const mascota = mascotaMap[row.id_mascota];
        if (!mascota) return row.id_mascota;
        const usuario = usuarioMap[mascota.id_usuario];
        return usuario ? `${usuario.nombre} ${usuario.apellido}` : '—';
      },
    },
    {
      key: 'id_mascota',
      label: 'Mascota',
      id: 'col-mascota',
      render: (_, row) => {
        const mascota = mascotaMap[row.id_mascota];
        return mascota?.nombre || row.id_mascota;
      },
    },
    {
      key: 'ubicacion',
      label: 'Coordenadas',
      id: 'col-coords',
      render: (v) => (
        <a
          href={`https://www.google.com/maps?q=${encodeURIComponent(v)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            color: 'var(--sp-primary)',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          <MapPin size={14} /> Ver en mapa <ExternalLink size={12} />
        </a>
      ),
    },
    {
      key: 'fecha_hora',
      label: 'Cuándo',
      id: 'col-cuando',
      render: (v) => tiempoRelativo(v),
    },
    {
      key: 'lugar',
      label: 'Dirección',
      id: 'col-direccion',
      render: (v) => v || 'Sin resolver',
    },
  ];

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.25rem' }}>
        Historial de ubicaciones
      </h1>

      {/* Filtros: búsqueda y fechas */}
      <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
        <input
          className="sp-input"
          type="text"
          placeholder="Buscar por dueño, mascota, dirección…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '280px' }}
        />
        <label style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          Desde:
          <input
            type="date"
            className="sp-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ maxWidth: '150px' }}
          />
        </label>
        <label style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          Hasta:
          <input
            type="date"
            className="sp-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ maxWidth: '150px' }}
          />
        </label>
      </div>

      {/* Botón de eliminación masiva */}
      {selectedIds.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <button
            className="sp-btn sp-btn--danger"
            onClick={handleBatchDelete}
          >
            Eliminar {selectedIds.length} seleccionada{selectedIds.length > 1 ? 's' : ''}
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        error={error}
        onEdit={null}
        onDelete={handleDelete}
        // props de selección
        selectable={true}
        selectedIds={selectedIds}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
      />

      {/* Confirmación eliminación individual */}
      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar la ubicación #${selected.id}?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {/* Confirmación eliminación masiva */}
      {showBatchDelete && (
        <DeleteConfirm
          message={`¿Eliminar ${selectedIds.length} ubicación(es)?`}
          onConfirm={confirmBatchDelete}
          onCancel={() => setShowBatchDelete(false)}
        />
      )}
    </div>
  );
};

export default UbicacionesPanel;